import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Inbox,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  MessageSquare,
  Flame,
  RefreshCw,
  XCircle,
  Users,
  Link as LinkIcon,
  Save,
  Trash2,
  Send,
  ChevronDown,
  ChevronUp,
  Code2,
  Copy,
  Check,
  AlertCircle,
  X,
} from "lucide-react";

const IST_DATE_FMT = { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" };
const IST_TIME_FMT = { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata", hour12: true };

function prettyDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", IST_DATE_FMT);
  } catch {
    return iso;
  }
}
function prettyTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", IST_TIME_FMT);
  } catch {
    return "";
  }
}

export default function Complaints() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [teamMemberOptions, setTeamMemberOptions] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  // Two-level filter: `scope` chooses the top-level tab, `filterValue`
  // is the specific team-member or agency name (empty for "All").
  const [scope, setScope] = useState("all"); // "all" | "team" | "agency"
  const [filterValue, setFilterValue] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  // Two-choice resolve dialog — mirrors the per-group Complaint Box UX.
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveChoice, setResolveChoice] = useState("new_dac");
  const [resolveNewDac, setResolveNewDac] = useState("");
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  // Iter83: Resolve All flow — modal typing gate + running undo window.
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // { batch_id, resolved_count, expires_at:number, remaining:number }
  const [bulkUndo, setBulkUndo] = useState(null);
  // Iter84: main-tab toggle — "open" (default) vs "resolved" history.
  const [mainTab, setMainTab] = useState("open");
  const [history, setHistory] = useState({ items: [], total: 0, window: null });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDays, setHistoryDays] = useState("7");
  const [historyType, setHistoryType] = useState("all"); // all | single | bulk | auto
  const [historyStaff, setHistoryStaff] = useState("");
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  // Iter85: agency filter (list of group_ids CSV) for Resolved tab.
  const [historyAgency, setHistoryAgency] = useState("");
  // Iter85: LPG/mobile search across Open + Resolved.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Iter83: 1-second countdown for the undo toast. Auto-dismisses when
  // the 10-minute window closes so the user can't click a stale Undo
  // (backend would 410 anyway; this just keeps the UI honest).
  useEffect(() => {
    if (!bulkUndo) return undefined;
    const t = setInterval(() => {
      setBulkUndo((prev) => {
        if (!prev) return null;
        const remaining = Math.max(0, Math.ceil((prev.expires_at - Date.now()) / 1000));
        if (remaining <= 0) return null;
        return { ...prev, remaining };
      });
    }, 1000);
    return () => clearInterval(t);
  }, [bulkUndo?.batch_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Iter83b: on Complaints page mount, ask the server for any active
  // bulk-resolve batch (within its 10-min window, not yet undone). If
  // found, restore the floating undo toast so the admin can still
  // undo even after navigating away or reloading the browser.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/complaints/active-bulk-batch");
        if (cancelled || !data?.active) return;
        setBulkUndo({
          batch_id: data.batch_id,
          resolved_count: data.count,
          expires_at: Date.now() + data.remaining_seconds * 1000,
          remaining: data.remaining_seconds,
        });
      } catch {
        // Non-privileged users get 200 {active:false}; ignore other errors.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(
    async (nextScope, nextValue) => {
      setErr("");
      const s = nextScope === undefined ? scope : nextScope;
      const v = nextValue === undefined ? filterValue : nextValue;
      const params = {};
      if (s === "team" && v) params.staff = v;
      if (s === "agency" && v) params.agency = v;
      try {
        const { data } = await api.get("/complaints", { params });
        setGroups(data.date_groups || []);
        setTeamMemberOptions(data.team_member_options || []);
        setAgencyOptions(data.agency_options || []);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [scope, filterValue]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Iter84: fetch resolved history on tab open / filter change.
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { days: historyDays, type: historyType };
      if (historyDays === "custom") {
        if (historyFrom) params.from_date = historyFrom;
        if (historyTo) params.to_date = historyTo;
      }
      if (historyStaff) params.staff = historyStaff;
      if (historyAgency) params.agency = historyAgency;
      const { data } = await api.get("/complaints/resolved-history", { params });
      setHistory({
        items: data.items || [],
        total: data.total || 0,
        window: data.window,
      });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  }, [historyDays, historyType, historyStaff, historyFrom, historyTo, historyAgency]);

  useEffect(() => {
    if (mainTab === "resolved") loadHistory();
  }, [mainTab, loadHistory]);

  // Iter85: debounced LPG/mobile search — hits `/complaints/search` when
  // the input has ≥4 digits. Clears results on empty/short input.
  useEffect(() => {
    const q = (searchQuery || "").trim();
    if (q.length < 4 || !/^\d+$/.test(q)) {
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get("/complaints/search", { params: { q } });
        if (cancelled) return;
        setSearchResults(data);
      } catch {
        if (!cancelled) setSearchResults({ query: q, total: 0, items: [] });
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchQuery]);

  const switchScope = (next) => {
    setScope(next);
    setFilterValue("");
    load(next, "");
  };

  const pickFilter = (value) => {
    setFilterValue(value);
    load(scope, value);
  };

  const isPrivileged = !!(user?.role === "admin" || user?.is_owner);

  const openResolveDialog = (it) => {
    setResolveTarget(it);
    setResolveChoice("new_dac");
    setResolveNewDac("");
  };
  const submitResolve = async () => {
    if (!resolveTarget) return;
    if (resolveChoice === "new_dac" && (resolveNewDac.length < 4 || resolveNewDac.length > 8)) {
      setErr("New DAC must be 4-8 digits");
      return;
    }
    setResolveSubmitting(true);
    const msgId = resolveTarget.message_id;
    try {
      // LPG entry-level complaints (wrong OR cancelled) — hit the
      // entry-scoped resolve so the exact row flips to resent with
      // the correct DAC / delivered stamp.
      if (
        (resolveTarget.kind === "lpg_wrong" || resolveTarget.kind === "lpg_cancelled")
        && typeof resolveTarget.entry_index === "number"
      ) {
        await api.post(
          `/groups/${resolveTarget.group_id}/messages/${msgId}/lpg/${resolveTarget.entry_index}/resolve`,
          {
            action: resolveChoice,
            new_dac: resolveChoice === "new_dac" ? resolveNewDac : undefined,
          }
        );
      } else {
        // Text or legacy complaint → message-level resolve endpoint.
        await api.post(`/complaints/${msgId}/resolve`, {
          action: resolveChoice,
          new_dac: resolveChoice === "new_dac" ? resolveNewDac : undefined,
        });
      }
      setResolveTarget(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setResolveSubmitting(false);
    }
  };

  const totalOpen = groups.reduce((acc, g) => acc + (g.items?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="complaints-page">
      <header className="bg-white border-b border-[#DCDCD3] px-6 py-3 flex items-center gap-3 sticky top-0 z-20">
        <button
          onClick={() => navigate("/home")}
          className="text-[#6B6B61] hover:text-[#1C1C19]"
          data-testid="complaints-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <Inbox className="w-5 h-5 text-[#C8553D]" />
          <h1
            className="text-lg font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Complaint Box
          </h1>
          <span className="ml-2 text-xs font-mono text-[#6B6B61]">
            {totalOpen} open
          </span>
        </div>
        {isPrivileged && (
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="🔍 LPG ID / Mobile…"
            data-testid="complaints-search-input"
            className="ml-2 px-2 py-1 text-xs border border-[#DCDCD3] rounded font-mono w-44 focus:outline-none focus:border-[#385C4B]"
          />
        )}
        <button
          onClick={load}
          data-testid="complaints-refresh-button"
          className="text-[#6B6B61] hover:text-[#385C4B] p-1.5 rounded hover:bg-[#385C4B]/5"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.75} />
        </button>
        {isPrivileged && totalOpen > 0 && (
          <button
            onClick={() => {
              setBulkConfirmText("");
              setBulkModalOpen(true);
            }}
            data-testid="complaints-resolve-all-button"
            className="ml-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2B4A3B]"
            title="Bulk clear every open complaint (ledger untouched)"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Resolve All ({totalOpen})
          </button>
        )}
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-5">
        {/* Iter85: LPG/Mobile search results — appears above the tabs
            whenever the header search input has 4+ digits. Same list
            for both Open + Resolved views. */}
        {isPrivileged && searchResults && (
          <div className="bg-white border-2 border-[#385C4B] rounded-lg p-3 space-y-2" data-testid="complaints-search-panel">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-[#385C4B]">
                🔍 Search: <span className="font-mono">{searchResults.query}</span>
                {" · "}
                <span className="text-[#6B6B61] font-normal">
                  {searchLoading ? "loading…" : `${searchResults.total} occurrence(s)`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults(null);
                }}
                data-testid="complaints-search-close"
                className="p-0.5 rounded hover:bg-[#F5F5F0] text-[#6B6B61]"
                title="Close search"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {searchResults.items.length === 0 ? (
              <div className="text-xs text-[#6B6B61] italic py-2">
                Kuch match nahi mila. Kya aapne poora LPG ID / mobile type kiya?
              </div>
            ) : (
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {searchResults.items.map((it, i) => {
                  const badge =
                    it.status === "bulk"
                      ? { label: "BULK RESOLVED", cls: "bg-purple-100 text-purple-800" }
                      : it.status === "auto"
                      ? { label: "AUTO RESOLVED", cls: "bg-blue-100 text-blue-800" }
                      : it.status === "resolved"
                      ? { label: "RESOLVED", cls: "bg-emerald-100 text-emerald-800" }
                      : it.status === "delivered"
                      ? { label: "DELIVERED", cls: "bg-slate-100 text-slate-800" }
                      : it.status === "pending"
                      ? { label: "PENDING", cls: "bg-amber-100 text-amber-800" }
                      : { label: "OPEN", cls: "bg-red-100 text-red-800" };
                  const ts = it.resolved_at || it.created_at;
                  let when = "";
                  try {
                    when = new Date(ts).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      day: "2-digit", month: "short",
                      hour: "2-digit", minute: "2-digit", hour12: true,
                    });
                  } catch {}
                  return (
                    <div key={`${it.message_id}-${i}`} className="bg-[#FAFAF7] rounded p-2 text-xs" data-testid={`search-result-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#1C1C19]">{it.agency}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${badge.cls}`}>{badge.label}</span>
                        <span className="text-[10px] text-[#6B6B61]">{when}</span>
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-[#6B6B61]">
                        LPG {it.lpg_id || "—"}
                        {it.dac_code ? ` · DAC ${it.dac_code}` : ""}
                        {it.mobile ? ` · Mobile ${it.mobile}` : ""}
                      </div>
                      {(it.resolved_by || it.note) && (
                        <div className="mt-0.5 text-[10px] text-[#6B6B61]">
                          {it.resolved_by && <>By <b>{it.resolved_by}</b></>}
                          {it.note && ` · ${it.note}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Iter84: main tab bar — Open (default) vs Resolved (history) */}
        {isPrivileged && (
          <div className="flex gap-1 border-b border-[#DCDCD3]" data-testid="complaints-main-tabs">
            <button
              type="button"
              data-testid="complaints-tab-open"
              onClick={() => setMainTab("open")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                mainTab === "open"
                  ? "border-[#385C4B] text-[#385C4B]"
                  : "border-transparent text-[#6B6B61] hover:text-[#1C1C19]"
              }`}
            >
              📥 Open ({totalOpen})
            </button>
            <button
              type="button"
              data-testid="complaints-tab-resolved"
              onClick={() => setMainTab("resolved")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                mainTab === "resolved"
                  ? "border-[#385C4B] text-[#385C4B]"
                  : "border-transparent text-[#6B6B61] hover:text-[#1C1C19]"
              }`}
            >
              ✅ Resolved{history.total > 0 ? ` (${history.total})` : ""}
            </button>
          </div>
        )}

        {mainTab === "resolved" ? (
          <ResolvedHistoryPanel
            history={history}
            loading={historyLoading}
            days={historyDays}
            setDays={setHistoryDays}
            typeFilter={historyType}
            setTypeFilter={setHistoryType}
            staffFilter={historyStaff}
            setStaffFilter={setHistoryStaff}
            fromDate={historyFrom}
            setFromDate={setHistoryFrom}
            toDate={historyTo}
            setToDate={setHistoryTo}
            agencyFilter={historyAgency}
            setAgencyFilter={setHistoryAgency}
            onRefresh={loadHistory}
          />
        ) : (
        <>
        <div className="text-xs text-[#6B6B61] leading-relaxed">
          {isPrivileged ? (
            <>
              Saari pending complaints (LPG wrong codes + text complaints) yahan
              date-wise dikhti hain. <b>Resolve</b> button se manually clear kar
              sakte hain, ya group ke chat me sahi DAC bhejne par automatic
              hat jayegi.
            </>
          ) : (
            <>
              Aapke group ki saari pending complaints yahan dikhengi. Sahi DAC
              aate hi entry automatically hat jayegi.
            </>
          )}
        </div>

        {/* Two-level filter — top pill tabs pick scope (All / Team Member
            / Agency), sub-row picks the specific value. Requested by user
            (Feb 2026): "FOLDER BY FOLDER DIKHE......ALL / TEAM MEMBER /
            AGENCY......PROFESSIONAL TYPE SE ARRANGE KAR K BANA DO" */}
        {(isPrivileged || teamMemberOptions.length + agencyOptions.length > 0) && (
          <div
            className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden"
            data-testid="complaints-filter-panel"
          >
            {/* Top-level scope tabs */}
            <div className="flex items-stretch border-b border-[#DCDCD3]">
              {[
                { key: "all", label: "All", count: -1 },
                { key: "team", label: "Team Member", count: teamMemberOptions.length },
                { key: "agency", label: "Agency", count: agencyOptions.length },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => switchScope(t.key)}
                  data-testid={`complaints-scope-tab-${t.key}`}
                  className={`flex-1 px-3 py-2.5 text-xs font-mono uppercase tracking-[0.15em] transition-colors relative ${
                    scope === t.key
                      ? "bg-[#385C4B] text-white"
                      : "bg-white text-[#1C1C19] hover:bg-[#F7F6EE]"
                  }`}
                >
                  {t.label}
                  {t.count > 0 && (
                    <span
                      className={`ml-1.5 text-[9px] font-mono px-1 py-0.5 rounded ${
                        scope === t.key
                          ? "bg-white/20 text-white"
                          : "bg-[#DCDCD3]/50 text-[#6B6B61]"
                      }`}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Sub-row (only when a scope is chosen) */}
            {scope === "team" && (
              <div
                className="p-3 flex items-center gap-2 flex-wrap bg-[#F7F6EE]/40"
                data-testid="complaints-team-subrow"
              >
                <Users className="w-3.5 h-3.5 text-[#385C4B]" strokeWidth={1.75} />
                <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] mr-1">
                  Select team member
                </span>
                {teamMemberOptions.length === 0 && (
                  <span className="text-[11px] text-[#6B6B61] italic">
                    Koi team member ka open complaint nahi
                  </span>
                )}
                {teamMemberOptions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => pickFilter(s)}
                    data-testid={`complaints-team-tab-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-xs font-mono px-2.5 py-1 rounded border transition-colors ${
                      filterValue.toLowerCase() === s.toLowerCase()
                        ? "bg-[#385C4B] text-white border-[#385C4B]"
                        : "bg-white text-[#1C1C19] border-[#DCDCD3] hover:border-[#385C4B]/40"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {scope === "agency" && (
              <div
                className="p-3 flex items-center gap-2 flex-wrap bg-[#F7F6EE]/40"
                data-testid="complaints-agency-subrow"
              >
                <Inbox className="w-3.5 h-3.5 text-[#385C4B]" strokeWidth={1.75} />
                <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] mr-1">
                  Select agency
                </span>
                {agencyOptions.length === 0 && (
                  <span className="text-[11px] text-[#6B6B61] italic">
                    Koi agency ka open complaint nahi
                  </span>
                )}
                {agencyOptions.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => pickFilter(a)}
                    data-testid={`complaints-agency-tab-${a.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-xs font-mono px-2.5 py-1 rounded border transition-colors ${
                      filterValue.toLowerCase() === a.toLowerCase()
                        ? "bg-[#385C4B] text-white border-[#385C4B]"
                        : "bg-white text-[#1C1C19] border-[#DCDCD3] hover:border-[#385C4B]/40"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}
            {scope === "all" && (
              <div className="p-3 text-[11px] text-[#6B6B61] bg-[#F7F6EE]/40">
                Sabhi open complaints — team members + agencies dono. Kisi ek team
                member ya agency ka alag view chahiye to upar wale tab pe click
                karo.
              </div>
            )}
          </div>
        )}

        {/* Per-team-member Google Sheet webhook routing (admin-only). */}
        {isPrivileged && <WebhookRoutingPanel />}
        {err && (
          <div className="bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] rounded-md px-3 py-2 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {err}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16 text-[#6B6B61]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {!loading && groups.length === 0 && !err && (
          <div
            className="bg-white border border-[#DCDCD3] rounded-lg p-10 text-center"
            data-testid="complaints-empty"
          >
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#385C4B] mb-3" />
            <div className="text-[#1C1C19] font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              No pending complaints
            </div>
            <div className="text-xs text-[#6B6B61] mt-1">
              Sab clear hai. Naya wrong-DAC ya complaint aate hi yahan dikhega.
            </div>
          </div>
        )}

        {!loading &&
          groups.map((g) => (
            <section key={g.date} data-testid="complaints-date-group">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61]">
                  {prettyDate(g.date + "T00:00:00+05:30")}
                </div>
                <div className="flex-1 h-px bg-[#DCDCD3]" />
                <div className="text-[10px] font-mono text-[#C8553D]">
                  {g.items.length} item{g.items.length > 1 ? "s" : ""}
                </div>
              </div>
              <div className="space-y-2">
                {g.items.map((it) => {
                  const isLpg = it.kind === "lpg_wrong" || it.kind === "lpg_cancelled";
                  const isCancelled = it.kind === "lpg_cancelled";
                  const themeCls = isCancelled
                    ? "bg-slate-400/10 text-slate-700"
                    : it.kind === "lpg_wrong"
                      ? "bg-[#C8553D]/10 text-[#C8553D]"
                      : "bg-[#385C4B]/10 text-[#385C4B]";
                  const kindLabel = isCancelled
                    ? "CANCELLED"
                    : it.kind === "lpg_wrong"
                      ? "WRONG DAC"
                      : "COMPLAINT";
                  return (
                  <article
                    key={it.key}
                    data-testid="complaint-item"
                    className="bg-white border border-[#DCDCD3] rounded-lg p-3 flex items-start gap-3 shadow-sm"
                  >
                    <div className={`p-2 rounded-md flex-shrink-0 ${themeCls}`}>
                      {isCancelled ? (
                        <XCircle className="w-4 h-4" strokeWidth={1.75} />
                      ) : it.kind === "lpg_wrong" ? (
                        <Flame className="w-4 h-4" strokeWidth={1.75} />
                      ) : (
                        <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-xs font-semibold text-[#1C1C19]"
                          style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                          {it.agency}
                        </span>
                        <span className="text-[10px] font-mono text-[#6B6B61]">
                          {prettyTime(it.filed_at || it.marked_at)}
                        </span>
                        <span
                          className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded ${themeCls}`}
                        >
                          {kindLabel}
                        </span>
                      </div>
                      {isLpg ? (
                        <div className="mt-1.5 font-mono text-sm text-[#1C1C19]">
                          <span className="text-[#6B6B61] text-xs">LPG ID </span>
                          {it.lpg_id}
                          <span className="text-[#6B6B61] text-xs ml-3">DAC </span>
                          <span className={`font-semibold ${isCancelled ? "text-slate-700" : "text-[#C8553D]"}`}>
                            {it.dac_code}
                          </span>
                        </div>
                      ) : (
                        <div className="mt-1.5 text-sm text-[#1C1C19] whitespace-pre-wrap break-words">
                          {it.text || "(no content)"}
                          {it.reply_to && (
                            <div className="mt-1 pl-2 border-l-2 border-[#385C4B] text-xs text-[#6B6B61]">
                              ↩ {it.reply_to.sender_display_name}:{" "}
                              <span className="italic">{it.reply_to.text}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-1 text-[10px] text-[#6B6B61]">
                        {isLpg ? "Marked by" : "Filed by"}{" "}
                        <b>{it.marked_by || it.filed_by || "—"}</b>
                        {it.marked_by_role === "delivery_man" && (
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-[#8A6046]/10 text-[#8A6046] font-semibold" data-testid="complaint-dm-tag">Delivery Man</span>
                        )}
                        {it.staff_name && (
                          <>
                            {" · Staff "}
                            <b className="text-[#385C4B]">{it.staff_name}</b>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {isPrivileged && (
                        <button
                          onClick={() => openResolveDialog(it)}
                          data-testid="complaint-resolve-button"
                          className="text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded bg-[#385C4B] text-white hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
                          Resolve
                        </button>
                      )}
                    </div>
                  </article>
                  );
                })}
              </div>
            </section>
          ))}
        </>
        )}
      </main>

      {resolveTarget && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => !resolveSubmitting && setResolveTarget(null)}
          data-testid="resolve-dialog"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md flex flex-col"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[#DCDCD3]">
              <h3 className="text-base font-semibold text-[#1C1C19] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                Resolve Complaint
              </h3>
              {resolveTarget.kind === "lpg_wrong" ? (
                <div className="text-[11px] font-mono text-[#6B6B61] mt-1">
                  <b className="text-[#1C1C19]">{resolveTarget.agency}</b>
                  <span className="mx-2">·</span>
                  LPG ID <b className="text-[#1C1C19]">{resolveTarget.lpg_id}</b>
                  <span className="mx-2">·</span>
                  Old DAC <b className="text-[#C8553D]">{resolveTarget.dac_code || "—"}</b>
                </div>
              ) : (
                <div className="text-[11px] font-mono text-[#6B6B61] mt-1 line-clamp-2 whitespace-pre-wrap">
                  <b className="text-[#1C1C19]">{resolveTarget.agency}</b>
                  {" · "}
                  {resolveTarget.text || "(no content)"}
                </div>
              )}
            </div>
            <div className="p-5 space-y-3">
              <label className={`block p-3 border rounded-md cursor-pointer transition-colors ${resolveChoice === "new_dac" ? "border-emerald-500 bg-emerald-50/50" : "border-[#DCDCD3] hover:border-[#385C4B]/40"}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="resolve-choice"
                    value="new_dac"
                    checked={resolveChoice === "new_dac"}
                    onChange={() => setResolveChoice("new_dac")}
                    data-testid="resolve-choice-new-dac"
                  />
                  <span className="text-sm font-semibold text-[#1C1C19]">New DAC bhejna hai</span>
                </div>
                <div className="text-[11px] text-[#6B6B61] mt-1 ml-6">
                  Correct DAC issue karo · agency ke chat me update ho jayega
                </div>
                {resolveChoice === "new_dac" && (
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={8}
                    value={resolveNewDac}
                    onChange={(ev) => setResolveNewDac(ev.target.value.replace(/\D/g, ""))}
                    placeholder="Enter new DAC (4-8 digits)"
                    data-testid="resolve-new-dac-input"
                    autoFocus
                    className="mt-2 ml-6 w-[calc(100%-1.5rem)] px-3 py-2 rounded-md border border-[#DCDCD3] font-mono text-sm tabular-nums focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                )}
              </label>
              <label className={`block p-3 border rounded-md cursor-pointer transition-colors ${resolveChoice === "delivered" ? "border-emerald-500 bg-emerald-50/50" : "border-[#DCDCD3] hover:border-[#385C4B]/40"}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="resolve-choice"
                    value="delivered"
                    checked={resolveChoice === "delivered"}
                    onChange={() => setResolveChoice("delivered")}
                    data-testid="resolve-choice-delivered"
                  />
                  <span className="text-sm font-semibold text-[#1C1C19]">Delivered from our side</span>
                </div>
                <div className="text-[11px] text-[#6B6B61] mt-1 ml-6">
                  Agency ne apne end se cylinder pahucha diya · complaint band karo
                </div>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2 bg-[#F7F6EE]">
              <button
                onClick={() => setResolveTarget(null)}
                disabled={resolveSubmitting}
                data-testid="resolve-cancel"
                className="px-3 py-1.5 text-xs font-mono rounded-md border border-[#DCDCD3] bg-white hover:bg-[#EFEEE6] text-[#1C1C19] disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={submitResolve}
                disabled={
                  resolveSubmitting ||
                  (resolveChoice === "new_dac" && (resolveNewDac.length < 4 || resolveNewDac.length > 8))
                }
                data-testid="resolve-submit"
                className="px-3 py-1.5 text-xs font-mono font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
              >
                {resolveSubmitting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                )}
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter83: Resolve All — confirmation modal */}
      {bulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4" data-testid="bulk-resolve-modal">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-base font-semibold text-[#1C1C19]">
                Resolve All Complaints
              </h3>
            </div>
            <p className="text-sm text-[#1C1C19] leading-relaxed">
              <b>{totalOpen}</b> pending complaints resolve honge.
              <br />
              <span className="text-emerald-700 font-semibold">
                Ledger me koi change nahi hoga
              </span>{" "}
              — sirf complaint status update hoga.
            </p>
            <div className="text-xs text-[#6B6B61]">
              Undo option 10 minute tak available rahega. Confirm karne ke liye niche{" "}
              <b className="font-mono text-[#385C4B]">RESOLVE</b> type kariye:
            </div>
            <input
              type="text"
              value={bulkConfirmText}
              onChange={(e) => setBulkConfirmText(e.target.value)}
              placeholder="Type RESOLVE"
              autoFocus
              data-testid="bulk-resolve-confirm-input"
              className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-[#385C4B]"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setBulkModalOpen(false)}
                disabled={bulkSubmitting}
                data-testid="bulk-resolve-cancel"
                className="px-3 py-1.5 text-xs font-semibold rounded border border-[#DCDCD3] hover:bg-[#F5F5F0]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkConfirmText !== "RESOLVE" || bulkSubmitting}
                onClick={async () => {
                  setBulkSubmitting(true);
                  try {
                    const { data } = await api.post("/complaints/resolve-all");
                    setBulkModalOpen(false);
                    setBulkConfirmText("");
                    if (data.batch_id) {
                      const expiresAt = Date.now() + 10 * 60 * 1000;
                      setBulkUndo({
                        batch_id: data.batch_id,
                        resolved_count: data.resolved,
                        expires_at: expiresAt,
                        remaining: 10 * 60,
                      });
                    }
                    await load();
                  } catch (e) {
                    setErr(e?.response?.data?.detail || "Bulk resolve failed");
                  } finally {
                    setBulkSubmitting(false);
                  }
                }}
                data-testid="bulk-resolve-submit"
                className="px-3 py-1.5 text-xs font-semibold rounded bg-[#385C4B] text-white hover:bg-[#2B4A3B] disabled:opacity-40 flex items-center gap-1.5"
              >
                {bulkSubmitting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                )}
                Resolve All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter83: Undo bulk resolve — floating toast with countdown */}
      {bulkUndo && (
        <div
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[#1C1C19] text-white px-4 py-2.5 rounded-lg shadow-xl"
          data-testid="bulk-resolve-undo-toast"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <div className="text-sm font-mono">
            <b>{bulkUndo.resolved_count}</b> complaints resolved
          </div>
          <span className="text-xs text-white/60">
            {Math.floor(bulkUndo.remaining / 60)}:
            {String(bulkUndo.remaining % 60).padStart(2, "0")} to undo
          </span>
          <button
            type="button"
            data-testid="bulk-resolve-undo-button"
            onClick={async () => {
              try {
                await api.post(`/complaints/bulk-undo/${bulkUndo.batch_id}`);
                setBulkUndo(null);
                await load();
              } catch (e) {
                setErr(e?.response?.data?.detail || "Undo failed");
              }
            }}
            className="text-xs font-semibold px-2.5 py-1 rounded bg-white text-[#1C1C19] hover:bg-white/90"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={() => setBulkUndo(null)}
            className="text-white/60 hover:text-white p-0.5"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Iter84: Resolved-history panel — inline on the Complaint Box page.
// Filters (Today / Last 7 / This month / Custom · Type · Staff) drive
// the same /complaints/resolved-history endpoint. Date-wise grouped
// cards. CSV + Excel export uses the on-screen filtered list so the
// downloaded file matches exactly what the admin sees.
// -----------------------------------------------------------------------------
function ResolvedHistoryPanel({
  history,
  loading,
  days,
  setDays,
  typeFilter,
  setTypeFilter,
  staffFilter,
  setStaffFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  agencyFilter,
  setAgencyFilter,
  onRefresh,
}) {
  const items = history.items || [];
  // Iter85: dynamic agency dropdown — de-dupe from the current items so
  // the picker only surfaces agencies that actually HAVE resolved rows
  // in this window/filter combination.
  const agencyOptions = useMemo(() => {
    const seen = new Map();
    for (const it of items) {
      if (it.group_id && it.agency && !seen.has(it.group_id)) {
        seen.set(it.group_id, it.agency);
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);
  // Iter86: dynamic staff dropdown — de-dupe from current resolved items
  // so the picker only surfaces staff who actually have resolved rows in
  // this window/filter combination (mirrors the agency dropdown behaviour).
  const staffOptions = useMemo(() => {
    const seen = new Set();
    for (const it of items) {
      const name = (it.staff_name || "").trim();
      if (name) seen.add(name);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [items]);
  // Group items by IST date.
  const byDate = new Map();
  for (const it of items) {
    let day = "";
    try {
      day = new Date(it.resolved_at).toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
      });
    } catch {
      day = (it.resolved_at || "").slice(0, 10);
    }
    if (!byDate.has(day)) byDate.set(day, []);
    byDate.get(day).push(it);
  }
  const grouped = [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]));

  const typeBadge = (t) => {
    if (t === "bulk") return { label: "BULK", cls: "bg-purple-100 text-purple-800" };
    if (t === "auto") return { label: "AUTO", cls: "bg-blue-100 text-blue-800" };
    return { label: "SINGLE", cls: "bg-emerald-100 text-emerald-800" };
  };

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };
  const fmtDate = (yyyymmdd) => {
    try {
      return new Date(yyyymmdd + "T00:00:00").toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return yyyymmdd;
    }
  };

  const copyText = async () => {
    if (items.length === 0) {
      toast.error("Nothing to copy");
      return;
    }
    const lines = [`*Resolved Complaints · ${items.length} total*`, ""];
    for (const [d, rows] of grouped) {
      lines.push(`── ${fmtDate(d)} · ${rows.length} resolved ──`);
      for (const r of rows) {
        lines.push(
          `[${typeBadge(r.type).label}] ${r.agency} · LPG ${r.lpg_id || "—"}` +
            (r.dac_code ? ` · DAC ${r.dac_code}` : "") +
            ` · ${fmtTime(r.resolved_at)} · by ${r.resolved_by || "—"}`
        );
      }
      lines.push("");
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadExcel = async () => {
    if (items.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    try {
      const ExcelJSMod = await import("exceljs");
      const ExcelJS = ExcelJSMod.default || ExcelJSMod;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Resolved");
      const header = ws.addRow([
        "Date", "Time", "Agency", "LPG ID", "DAC", "Type", "Resolved By", "Note",
      ]);
      header.eachCell((c) => {
        c.font = { bold: true, color: { argb: "FFFFFFFF" } };
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF385C4B" } };
      });
      for (const r of items) {
        const d = (r.resolved_at || "").slice(0, 10);
        ws.addRow([
          d, fmtTime(r.resolved_at), r.agency, r.lpg_id, r.dac_code,
          (r.type || "").toUpperCase(), r.resolved_by, r.note,
        ]);
      }
      ws.columns.forEach((col) => {
        let max = 10;
        col.eachCell({ includeEmpty: false }, (c) => {
          const l = String(c.value ?? "").length;
          if (l > max) max = l;
        });
        col.width = Math.min(max + 2, 40);
      });
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resolved_complaints_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Export failed");
      console.error(e);
    }
  };

  return (
    <div data-testid="resolved-history-panel" className="space-y-4">
      {/* Filters + Export */}
      <div className="bg-white border border-[#DCDCD3] rounded-lg p-3 flex flex-wrap gap-2 items-center">
        <select
          value={days}
          onChange={(e) => setDays(e.target.value)}
          data-testid="history-days"
          className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded"
        >
          <option value="1">Today</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="month">This month</option>
          <option value="999999">All time</option>
          <option value="custom">Custom range…</option>
        </select>
        {days === "custom" && (
          <>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded" />
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded" />
          </>
        )}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="history-type"
          className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded"
        >
          <option value="all">All types</option>
          <option value="single">Single</option>
          <option value="bulk">Bulk</option>
          <option value="auto">Auto</option>
        </select>
        <select
          value={agencyFilter || ""}
          onChange={(e) => setAgencyFilter(e.target.value)}
          data-testid="history-agency"
          className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded max-w-[180px]"
          title="Filter by agency"
        >
          <option value="">All agencies</option>
          {agencyOptions.map(([gid, name]) => (
            <option key={gid} value={gid}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={staffFilter}
          onChange={(e) => setStaffFilter(e.target.value)}
          data-testid="history-staff"
          className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded max-w-[160px]"
          title="Filter by staff"
        >
          <option value="">All staff</option>
          {staffOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="history-refresh"
          className="px-2 py-1.5 text-xs border border-[#DCDCD3] rounded hover:bg-[#F5F5F0] disabled:opacity-50"
        >
          {loading ? "…" : "Refresh"}
        </button>
        <div className="flex-1" />
        <div className="text-xs text-[#6B6B61] mr-2">
          <b>{items.length}</b> resolved
        </div>
        <button
          onClick={copyText}
          data-testid="history-copy"
          className="px-3 py-1.5 text-xs font-semibold border border-[#385C4B] text-[#385C4B] rounded hover:bg-[#EDF3F0] flex items-center gap-1"
        >
          <Copy className="w-3 h-3" />
          Copy
        </button>
        <button
          onClick={downloadExcel}
          data-testid="history-excel"
          className="px-3 py-1.5 text-xs font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2B4A3B] flex items-center gap-1"
        >
          <Save className="w-3 h-3" />
          Excel
        </button>
      </div>

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-[#DCDCD3] rounded-lg p-8 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <div className="text-sm font-semibold text-[#1C1C19]">No resolved complaints in this window</div>
          <div className="text-xs text-[#6B6B61] mt-1">Try widening the date range or clearing filters.</div>
        </div>
      )}

      {/* Grouped resolved cards */}
      {grouped.map(([day, rows]) => (
        <section key={day} data-testid={`history-day-${day}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-mono font-semibold text-[#6B6B61] tracking-wider uppercase">
              {fmtDate(day)}
            </div>
            <div className="flex-1 border-b border-[#DCDCD3]" />
            <div className="text-[10px] font-mono text-[#8A6046]">
              {rows.length} resolved
            </div>
          </div>
          <div className="space-y-1.5">
            {rows.map((r, i) => {
              const b = typeBadge(r.type);
              return (
                <div
                  key={`${r.message_id}-${r.lpg_id}-${i}`}
                  data-testid={`history-item-${i}`}
                  className="bg-white border border-[#DCDCD3] rounded p-2.5 flex items-start gap-2 text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1C1C19]">
                        {r.agency || "—"}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${b.cls}`}>
                        {b.label}
                      </span>
                      <span className="text-[10px] text-[#6B6B61]">
                        {fmtTime(r.resolved_at)}
                      </span>
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-[#6B6B61]">
                      LPG {r.lpg_id || "—"}
                      {r.dac_code ? ` · DAC ${r.dac_code}` : ""}
                    </div>
                    <div className="mt-0.5 text-[10px] text-[#6B6B61]">
                      Resolved by <b>{r.resolved_by || "—"}</b>
                      {r.note ? ` · ${r.note}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Google Apps Script (v2) — hardcoded so admins can copy-paste it right from
// the routing panel without hunting through docs / chat history. Reads the
// target tab from payload.sheet (with fallbacks) so admin can rename the tab
// in the UI without touching the script.
// -----------------------------------------------------------------------------
const APPS_SCRIPT_V2 = `/**
 * XYZ Chatting · COMPLAIN SHEET auto-sync (v3 · Feb 2026)
 *
 * v3 changes:
 *   - Handles BOTH new complaints (append) and resolutions (update-in-place)
 *     via the "sync_complaint" action. When a complaint gets resolved, the
 *     ORIGINAL row's Status + Note columns update in place — no more
 *     duplicate "resolve" rows appearing below the "lpg_wrong" row.
 *   - Falls back gracefully to append-only mode for v1/v2 payloads.
 *   - Tab name is still read from payload.sheet (fallback "COMPLAIN SHEET").
 *
 * Setup:
 *   1. Open your complaint Google Sheet -> Extensions -> Apps Script
 *   2. Paste this code -> Save -> Deploy -> Manage deployments -> Edit ->
 *      New version -> Deploy. URL stays the same.
 */
const DEFAULT_COMPLAINT_TAB = "COMPLAIN SHEET";
const COMPLAINT_HEADER = [
  "Complaint Time","Kind","LPG Id","Agency","DAC","Booking Date",
  "Marked / Filed By","Staff","Status","Note","Message Id"
];
// Column indices (1-based, matching sheet A/B/C…) used for updates.
const COL = {
  complaint_at: 1, kind: 2, lpg_id: 3, agency: 4, dac_code: 5,
  booking_date: 6, marked_by: 7, staff_name: 8, status: 9,
  text: 10, message_id: 11,
};

function doPost(e) {
  const lock = LockService.getScriptLock();
  lock.waitLock(20000);
  try {
    const body = JSON.parse(e.postData.contents);
    const appendRows = body.rows || [];
    const updateRows = body.updates || [];

    // Tab name (fall back to default).
    const tabName = (
      body.sheet || body.sheet_name || body.tab ||
      body.target_sheet || DEFAULT_COMPLAINT_TAB
    );

    const ss = SpreadsheetApp.getActiveSpreadsheet();
    let sh = ss.getSheetByName(tabName);
    if (!sh) sh = ss.insertSheet(tabName);
    if (sh.getLastRow() < 1) {
      sh.appendRow(COMPLAINT_HEADER);
    } else {
      sh.getRange(1, 1, 1, COMPLAINT_HEADER.length).setValues([COMPLAINT_HEADER]);
    }

    // 1) UPDATE in-place: for each resolution row, locate the original
    //    complaint row by (message_id + lpg_id) and rewrite its Status
    //    + Note columns. If nothing matches, fall through to append so
    //    no data is lost.
    let updated = 0;
    const spilloverToAppend = [];
    if (updateRows.length && sh.getLastRow() > 1) {
      const data = sh.getRange(2, 1, sh.getLastRow() - 1, COMPLAINT_HEADER.length).getValues();
      updateRows.forEach(function (u) {
        const uMsgId = String(u.message_id || "").trim();
        const uLpg = String(u.lpg_id || "").trim();
        let matched = false;
        for (let i = data.length - 1; i >= 0; i--) { // newest match wins
          const row = data[i];
          if (
            String(row[COL.message_id - 1] || "").trim() === uMsgId &&
            String(row[COL.lpg_id - 1] || "").trim() === uLpg
          ) {
            const rowNum = i + 2; // header offset
            sh.getRange(rowNum, COL.status).setValue(u.status || "resolved");
            sh.getRange(rowNum, COL.text).setValue(u.text || "");
            sh.getRange(rowNum, COL.marked_by).setValue(u.marked_by || "");
            sh.getRange(rowNum, COL.complaint_at).setValue(u.complaint_at || "");
            updated++;
            matched = true;
            break;
          }
        }
        if (!matched) spilloverToAppend.push(u); // no original found → append
      });
    } else {
      // No existing rows to match against — all updates become appends.
      updateRows.forEach(function (u) { spilloverToAppend.push(u); });
    }

    // 2) APPEND new complaints + un-matched updates.
    const toAppend = appendRows.concat(spilloverToAppend);
    const matrix = toAppend.map(function (r) {
      return [
        r.complaint_at || "",
        r.kind || "",
        r.lpg_id || "",
        r.agency || "",
        r.dac_code || "",
        r.booking_date || "",
        r.marked_by || "",
        r.staff_name || "",
        r.status || "",
        r.text || "",
        r.message_id || "",
      ];
    });
    if (matrix.length) {
      sh.getRange(sh.getLastRow() + 1, 1, matrix.length, COMPLAINT_HEADER.length)
        .setValues(matrix);
    }
    return out({
      ok: true,
      action: "sync_complaint",
      appended: matrix.length,
      updated: updated,
      tab: tabName,
    });
  } catch (err) {
    return out({ ok: false, error: String(err) });
  } finally { lock.releaseLock(); }
}

function out(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}
`;


// -----------------------------------------------------------------------------
// Apps Script viewer — collapsible code block with a one-tap Copy button. The
// button copies the RAW script even when the code is hidden. Requested by user
// (Feb 2026): "CODE HIDE K SATH, JAB COPY KARU TO DIKHE CODE / DIRECT CODE V
// COPY HO JAY".
// -----------------------------------------------------------------------------
function AppsScriptViewer() {
  const [showCode, setShowCode] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(APPS_SCRIPT_V2);
      } else {
        // Legacy fallback for insecure contexts
        const ta = document.createElement("textarea");
        ta.value = APPS_SCRIPT_V2;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Apps Script code copied · paste in your Google Sheet's Extensions → Apps Script");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Copy failed — please select the code and copy manually");
    }
  };

  return (
    <div className="bg-white border border-[#DCDCD3] rounded p-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Code2 className="w-4 h-4 text-[#385C4B]" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Google Apps Script (v3 · resolve-in-place)
          </div>
          <div className="text-[10px] text-[#6B6B61]">
            Copy karo → aapke Sheet ke Extensions → Apps Script me paste karo → Deploy karo.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowCode((v) => !v)}
          data-testid="apps-script-toggle"
          className="px-2 py-1.5 rounded border border-[#DCDCD3] bg-white text-[10px] font-mono uppercase tracking-[0.1em] hover:border-[#385C4B]/40 flex items-center gap-1"
        >
          {showCode ? (
            <>
              <ChevronUp className="w-3 h-3" strokeWidth={2} /> Hide
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" strokeWidth={2} /> Show
            </>
          )}
        </button>
        <button
          type="button"
          onClick={copy}
          data-testid="apps-script-copy"
          className={`px-2 py-1.5 rounded text-[10px] font-mono uppercase tracking-[0.1em] flex items-center gap-1 ${
            copied
              ? "bg-emerald-600 text-white"
              : "bg-[#385C4B] text-white hover:bg-[#2D4A3C]"
          }`}
          title="Copy full code to clipboard (works even when hidden)"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3" strokeWidth={2.5} /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" strokeWidth={2} /> Copy Code
            </>
          )}
        </button>
      </div>
      {showCode && (
        <pre
          data-testid="apps-script-code"
          className="bg-[#1C1C19] text-[#E8E7DF] text-[11px] leading-relaxed font-mono rounded p-3 overflow-x-auto max-h-[420px] overflow-y-auto"
          style={{ fontFamily: "Menlo, Consolas, monospace", tabSize: 2 }}
        >
          {APPS_SCRIPT_V2}
        </pre>
      )}
      <div className="text-[10px] text-[#6B6B61] mt-2 leading-relaxed">
        Deploy ke baad Web-app URL copy karo aur upar apne team member ke row me paste karke <b>Save</b> daba do.
        Fir <b>Test</b> button dabao — Sheet me test row 2-5 second me aa jayegi ✓
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Admin-only: manage each team member's OWN Google Sheet webhook URL.
// A complaint filed by Ashish auto-routes to Ashish's sheet, Komal's to
// Komal's — etc.  Falls back to the global URL if a user hasn't set one.
// -----------------------------------------------------------------------------
function WebhookRoutingPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({}); // { user_id: "url" }
  const [savingId, setSavingId] = useState("");
  const [testingId, setTestingId] = useState("");
  // Target tab-name inside every destination workbook (default "COMPLAIN SHEET")
  const [tabName, setTabName] = useState("COMPLAIN SHEET");
  const [tabDraft, setTabDraft] = useState("COMPLAIN SHEET");
  const [savingTab, setSavingTab] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [routing, settings] = await Promise.all([
        api.get("/complaints/webhook-routing"),
        api.get("/admin/settings"),
      ]);
      setData(routing.data);
      const nextDrafts = {};
      (routing.data.team_members || []).forEach((m) => {
        nextDrafts[m.user_id] = m.complaint_sheet_webhook_url || "";
      });
      setDrafts(nextDrafts);
      const t = settings.data.complaint_sheet_tab_name || "COMPLAIN SHEET";
      setTabName(t);
      setTabDraft(t);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !data) load();
  }, [open, data, load]);

  const save = async (m) => {
    const url = (drafts[m.user_id] || "").trim();
    if (url && !/^https?:\/\//i.test(url)) {
      toast.error("URL http:// ya https:// se start hona chahiye");
      return;
    }
    setSavingId(m.user_id);
    try {
      await api.put(`/complaints/webhook-routing/${m.user_id}`, {
        webhook_url: url,
      });
      toast.success(
        url
          ? `Webhook saved for ${m.staff_name || m.display_name}`
          : `Webhook cleared for ${m.staff_name || m.display_name} — will use global`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId("");
    }
  };

  const clear = async (m) => {
    setDrafts((prev) => ({ ...prev, [m.user_id]: "" }));
    await save({ ...m, user_id: m.user_id });
  };

  const testOne = async (m) => {
    setTestingId(m.user_id);
    try {
      const { data: resp } = await api.post(
        `/complaints/webhook-routing/${m.user_id}/test`
      );
      toast.success(
        `Test row bheja "${resp.tab || "COMPLAIN SHEET"}" tab me for ${resp.staff}${
          resp.used_fallback ? " (using GLOBAL fallback)" : ""
        } · HTTP ${resp.status_code} ✓`
      );
    } catch (e) {
      toast.error(formatApiError(e), { duration: 8000 });
    } finally {
      setTestingId("");
    }
  };

  const saveTab = async () => {
    const tab = (tabDraft || "").trim();
    if (!tab) {
      toast.error("Tab name blank nahi ho sakta");
      return;
    }
    setSavingTab(true);
    try {
      await api.put("/admin/settings", { complaint_sheet_tab_name: tab });
      setTabName(tab);
      toast.success(`Target tab set to "${tab}" — sab complaints yahin land karengi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingTab(false);
    }
  };

  return (
    <section
      className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden"
      data-testid="complaints-webhook-routing-panel"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="webhook-routing-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-[#F7F6EE] transition-colors"
      >
        <LinkIcon className="w-4 h-4 text-[#385C4B]" strokeWidth={1.75} />
        <div className="flex-1">
          <div
            className="text-sm font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Google Sheet Routing — per team member
          </div>
          <div className="text-[11px] text-[#6B6B61] mt-0.5">
            Har staff ka complaint uske apne Google Sheet me jaayega. Blank
            chhodne pe global sheet me fallback hoga.
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-[#6B6B61]" strokeWidth={1.75} />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#6B6B61]" strokeWidth={1.75} />
        )}
      </button>

      {open && (
        <div className="border-t border-[#DCDCD3] p-4 space-y-3 bg-[#F7F6EE]/40">
          {loading && (
            <div className="flex items-center justify-center py-6 text-[#6B6B61]">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}

          {!loading && data && (
            <>
              {/* Google Apps Script — visible + copy-paste ready */}
              <AppsScriptViewer />

              {/* Target Tab name — every complaint webhook payload carries
                  this so the receiving Google Sheet always writes to the
                  same tab (e.g. "COMPLAIN SHEET") no matter how many
                  other tabs the workbook has. */}
              <div className="bg-white border border-[#DCDCD3] rounded p-3 text-[11px]">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono tracking-[0.15em] uppercase text-[#6B6B61] text-[10px]">
                    Target Tab (all sheets)
                  </span>
                  {tabName === tabDraft.trim() ? (
                    <span className="text-emerald-700 font-mono text-[10px]">
                      · saved
                    </span>
                  ) : (
                    <span className="text-amber-700 font-mono text-[10px]">
                      · unsaved
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={tabDraft}
                    onChange={(e) => setTabDraft(e.target.value)}
                    placeholder="COMPLAIN SHEET"
                    data-testid="webhook-tab-name-input"
                    className="flex-1 min-w-[180px] px-2.5 py-1.5 border border-[#DCDCD3] rounded text-xs font-mono focus:outline-none focus:border-[#385C4B]"
                  />
                  <button
                    type="button"
                    onClick={saveTab}
                    disabled={savingTab || tabName === tabDraft.trim() || !tabDraft.trim()}
                    data-testid="webhook-tab-name-save"
                    className="px-2 py-1.5 rounded bg-[#385C4B] text-white text-[10px] font-mono uppercase tracking-[0.1em] hover:bg-[#2D4A3C] disabled:opacity-40 flex items-center gap-1"
                  >
                    {savingTab ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Save className="w-3 h-3" strokeWidth={2} />
                    )}
                    Save Tab
                  </button>
                </div>
                <div className="text-[10px] text-[#6B6B61] mt-1.5 leading-relaxed">
                  Har complaint row is naam ke tab me hi jaayegi — chahe target sheet me
                  kitne bhi tabs hon (<code className="font-mono">resend</code>,{" "}
                  <code className="font-mono">Sheet4</code>, etc.). Google Apps Script me
                  <b> payload.sheet</b> / <b>payload.sheet_name</b> / <b>payload.tab</b> —
                  koi bhi field padho, sab me yahi naam bhejenge.
                </div>
              </div>

              {/* Global fallback URL — read-only reminder (edit in Admin) */}
              <div className="bg-white border border-[#DCDCD3] rounded p-3 text-[11px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono tracking-[0.15em] uppercase text-[#6B6B61] text-[10px]">
                    Global Fallback
                  </span>
                  {data.global_url ? (
                    <span className="text-emerald-700 font-mono text-[10px]">
                      · configured
                    </span>
                  ) : (
                    <span className="text-amber-700 font-mono text-[10px]">
                      · not set
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-[#1C1C19] break-all">
                  {data.global_url || (
                    <span className="text-[#6B6B61] italic">
                      (empty · unrouted staff complaints will be dropped)
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#6B6B61] mt-1">
                  Edit in <b>Admin → Sheets → Complaint Sheet URL</b>.
                </div>
              </div>

              {(data.team_members || []).length === 0 && (
                <div className="text-center text-xs text-[#6B6B61] py-4">
                  Koi team member abhi tak configure nahi hua. `staff_name`
                  set karo Admin → Users me.
                </div>
              )}

              <div className="space-y-2">
                {(data.team_members || []).map((m) => {
                  const draft = drafts[m.user_id] ?? "";
                  const currentUrl = m.complaint_sheet_webhook_url || "";
                  const dirty = draft.trim() !== currentUrl.trim();
                  return (
                    <div
                      key={m.user_id}
                      data-testid={`webhook-row-${m.user_id}`}
                      className="bg-white border border-[#DCDCD3] rounded-md p-3 flex flex-col gap-2 sm:flex-row sm:items-center"
                    >
                      <div className="sm:w-44 flex-shrink-0">
                        <div
                          className="text-sm font-semibold text-[#1C1C19] flex items-center gap-1.5"
                          style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                          {m.staff_name || m.display_name || m.username}
                          {m.has_open_complaint && (
                            <span
                              className="text-[9px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#C8553D]/10 text-[#C8553D]"
                              title="Has at least one open complaint right now"
                            >
                              live
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-[#6B6B61] mt-0.5">
                          {m.username} · {m.role}
                        </div>
                      </div>
                      <div className="flex-1 flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <input
                          type="url"
                          value={draft}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [m.user_id]: e.target.value,
                            }))
                          }
                          placeholder="https://script.google.com/macros/... (blank = use global)"
                          data-testid={`webhook-url-input-${m.user_id}`}
                          className="flex-1 min-w-0 px-2.5 py-1.5 border border-[#DCDCD3] rounded text-xs font-mono focus:outline-none focus:border-[#385C4B]"
                        />
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => save(m)}
                            disabled={savingId === m.user_id || !dirty}
                            data-testid={`webhook-save-${m.user_id}`}
                            title={dirty ? "Save" : "No changes"}
                            className="px-2 py-1.5 rounded bg-[#385C4B] text-white text-[10px] font-mono uppercase tracking-[0.1em] hover:bg-[#2D4A3C] disabled:opacity-40 flex items-center gap-1"
                          >
                            {savingId === m.user_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Save className="w-3 h-3" strokeWidth={2} />
                            )}
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => testOne(m)}
                            disabled={testingId === m.user_id}
                            data-testid={`webhook-test-${m.user_id}`}
                            title="Send a test row"
                            className="px-2 py-1.5 rounded bg-white border border-[#DCDCD3] text-[#1C1C19] text-[10px] font-mono uppercase tracking-[0.1em] hover:border-[#385C4B]/40 disabled:opacity-40 flex items-center gap-1"
                          >
                            {testingId === m.user_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3" strokeWidth={2} />
                            )}
                            Test
                          </button>
                          {currentUrl && (
                            <button
                              type="button"
                              onClick={() => clear(m)}
                              disabled={savingId === m.user_id}
                              data-testid={`webhook-clear-${m.user_id}`}
                              title="Clear · fall back to global"
                              className="px-2 py-1.5 rounded bg-white border border-[#C8553D]/30 text-[#C8553D] text-[10px] font-mono uppercase tracking-[0.1em] hover:bg-[#C8553D]/5 disabled:opacity-40 flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(data.unmatched_staff || []).length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2.5 text-[11px]">
                  <div className="font-semibold text-amber-800 mb-1">
                    Unmatched staff identities in current complaints:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {data.unmatched_staff.map((s) => (
                      <span
                        key={s}
                        className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-amber-500/30 text-amber-800"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-amber-700 mt-1.5">
                    Ye names kisi user account se match nahi ho rahe. Admin →
                    Users me create karo (matching <b>staff_name</b> ke saath)
                    to inke complaints alag sheet me route ho sakein.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
